;(function () {
  'use strict'

  angular
    .module('contrast.filtering')
    .factory('FeaturesService', FeaturesService)

  function FeaturesService(
    $rootScope,
    $q,
    FeaturesFactory,
    ServiceUtils,
    LEGACY_AGENT_LANGUAGE
  ) {
    'ngInject'

    var me = this
    me.featureFlags = null
    me.featureFlagsPromise = null
    me.agentFeatures = null
    me.agentFeaturesPromise = null

    return {
      isFeatureActive: isFeatureActive,
      loadFeatureFlags: loadFeatureFlags,
      loadAgentFeatures: loadAgentFeatures,
      isAgentFeatureEnabled: isAgentFeatureEnabled,
      getEnabledLanguagesByAgentFeature: getEnabledLanguagesByAgentFeature,
      isFeatureInAllLanguages: isFeatureInAllLanguages,
      getRbacStatus: getRbacStatus,
      getAll: getAll,
      isSplitFeatureFlagOn
    }

    function getAll() {
      return me.agentFeatures
    }

    function isFeatureActive(featureKey) {
      var keys = [featureKey]
      return FeaturesFactory.getFeaturesByKeys(keys).then(function (response) {
        return (
          response &&
          response.data &&
          response.data.length > 0 &&
          response.data[0].active
        )
      })
    }

    function loadFeatureFlags() {
      if (me.featureFlagsPromise) {
        return $q.when(me.featureFlags)
      } else if (me.featureFlagsPromise !== null) {
        return me.featureFlagsPromise
      }

      me.featureFlagsPromise = FeaturesFactory.getFeatures().then(function (
        flags
      ) {
        me.featureFlags = flags.data
        $rootScope.$broadcast('featureFlags:loaded', flags.data)
        return me.featureFlags
      })
      return me.featureFlagsPromise
    }

    function loadAgentFeatures() {
      if (me.agentFeaturesPromise) {
        return $q.when(me.agentFeatures)
      } else if (me.agentFeaturesPromise !== null) {
        return me.agentFeaturesPromise
      }

      me.agentFeaturesPromise = FeaturesFactory.getAgentFeatures()
        .then(function (response) {
          me.agentFeatures = response.data.features
          $rootScope.$broadcast('agentFeatures:loaded', me.agentFeatures)
          return me.agentFeatures
        })
        .catch(ServiceUtils.handleError)

      return me.agentFeaturesPromise
    }

    function isAgentFeatureEnabled(featureName, agentLanguage, skipLoad) {
      var agentLanguages = angular.isArray(agentLanguage)
        ? agentLanguage
        : [agentLanguage]
      var featureNames = angular.isArray(featureName)
        ? featureName
        : [featureName]
      if (skipLoad) {
        return _areLanguagesEnabled(
          featureNames,
          agentLanguages
        )(me.agentFeatures)
      }
      return loadAgentFeatures().then(
        _areLanguagesEnabled(featureNames, agentLanguages)
      )
    }

    function getEnabledLanguagesByAgentFeature(featureName, skipLoad) {
      if (skipLoad) {
        return _getEnabledLanguagesByFeatureName(featureName)()
      }
      return loadAgentFeatures().then(
        _getEnabledLanguagesByFeatureName(featureName)
      )
    }

    function isSplitFeatureFlagOn(flagName) {
      if ($rootScope.splitClient) {
        const treatment = $rootScope.splitClient.getTreatment(flagName)
        return treatment === 'on'
      } else {
        // eslint-disable-next-line angular/log, no-console
        console.warn(
          'Split client not available while fetching treatment for',
          flagName
        )
        return false
      }
    }

    function _getEnabledLanguagesByFeatureName(featureName) {
      return function () {
        var feature = _getAgentFeatureByName(featureName)
        if (feature) {
          return _getTransformedLanguages(feature.languages)
        }
        return []
      }
    }

    function _getTransformedLanguages(languages) {
      return languages.map(function (language) {
        return LEGACY_AGENT_LANGUAGE[language]
      })
    }

    function _getAgentFeatureByName(featureName) {
      return me.agentFeatures.find(function (item) {
        return item.name === featureName
      })
    }

    function _areLanguagesEnabled(featureNames, agentLanguages) {
      return function (agentFeatures) {
        var i = 0
        for (; i < featureNames.length; i++) {
          var agentFeature = agentFeatures.find(function (item) {
              return item.name === featureNames[i]
            }),
            j = 0

          if (!agentFeature) {
            break
          }

          for (; j < agentLanguages.length; j++) {
            if (
              angular.isDefined(
                agentFeature.languages.find(
                  _findAgentLanguage(agentLanguages[j])
                )
              )
            ) {
              return true
            }
          }
        }

        return false
      }
    }

    function _findAgentLanguage(agentLanguage) {
      return function (lang) {
        return (
          lang === agentLanguage ||
          LEGACY_AGENT_LANGUAGE[lang].indexOf(agentLanguage) > -1
        )
      }
    }

    function isFeatureInAllLanguages(featureName, languages) {
      var enabledLanguages = getEnabledLanguagesByAgentFeature(
        featureName,
        true
      )
      if (enabledLanguages.length != languages.length) {
        return false
      }

      var i = 0,
        j = 0,
        exists = false
      for (; i < languages.length; i++) {
        for (; j < enabledLanguages.length; j++) {
          exists = _.find(enabledLanguages[j], function (item) {
            return item === languages[i]
          })

          if (exists) {
            break
          }
        }

        if (!exists) {
          return false
        }
      }

      return true
    }

    function getRbacStatus(manuallyProvidedTreatments) {
      const disabledReturnValue = {
        enabled: false,
        isEnforced: false,
        splitFlag: '',
        isScanEnforced: false,
        isAssessEnforced: false,
        isRbacSSOProvisioningEnabled: false
      }

      /**
       * If both treatments from rootScope are not present _and_
       * no treatments are manually provided, then return disabled
       */
      if (!$rootScope.treatments && !manuallyProvidedTreatments) {
        return disabledReturnValue
      }

      const treatmentStore = $rootScope.treatments || manuallyProvidedTreatments

      /**
       * RBAC is considered enabled if either the Scan RBAC flag is
       * set (org must have `sast_enabled` set to `true`) or
       * if the base Assess flag is "on". Due to Split licensing changes,
       * the original Sast flag was able to be created with 3 treatments,
       * whereas the Assess flag could only have 2 while 3 was required for
       * the feature checks. Checking Assess RBAC enablement is now
       * consuming 2 separate flags.
       *
       * This utility is to help ease the overhead of the general check
       * to see if RBAC is on for an org and then to find the treatment
       * to use for Angular app visibility/functionality.
       */
      // New flags for RBAC
      const rbacEnabledFlag = treatmentStore['PROD-2415-rbac_service_enabled']
      const rbacAssessFlag =
        treatmentStore['PROD-2415-rbac_service_enforce_assess']
      const rbacScanFlag = treatmentStore['PROD-2415-rbac_service_enforce_scan']
      const rbacSSOProvisioningFlag =
        treatmentStore['PROD-2035_rbac_sso_user_provisioning']

      /**
       * Rbac is not enabled if no current org is returned
       * or if organization is freemium
       */
      if (
        !$rootScope.currentOrganization ||
        $rootScope.currentOrganization.isFreemium
      ) {
        return disabledReturnValue
      }
      // If sast is enabled check sast flag, otherwise check assess flag
      return {
        enabled: rbacEnabledFlag === 'on',
        isEnforced: rbacAssessFlag === 'on',
        splitFlag: $rootScope.currentOrganization.isSastEnabled
          ? 'PROD-2415-rbac_service_enforce_scan'
          : 'PROD-2415-rbac_service_enforce_assess',
        isScanEnforced:
          $rootScope.currentOrganization.isSastEnabled && rbacScanFlag === 'on',
        isAssessEnforced: rbacAssessFlag === 'on',
        isRbacSSOProvisioningEnabled: rbacSSOProvisioningFlag === 'on'
      }
    }
  }
})()
